/* Copyright (c) 2017 Backpack Health LLC */

import React, { FC } from 'react';
import classnames from 'classnames';

import IconAttachment from './icons/IconAttachment';
import COLORS, { colorClassNames } from '../styles/Colors';
import { Icons } from '../../bphComponents/bundle';
import { FlaggedIcon } from './FlagToggle';
import { IconSlot } from '../views/components/IconSlot';
import HealthEntryMetaIcons from '../views/components/attributes/HealthEntryMetaIcons';

interface Props {
  color?: string;
  title: any;
  additionalTitle: any;
  subTitle: any;
  areIconsAttachedToRight: boolean;
  hasAttachments: boolean;
  hasComments: boolean;
  isFlagged: boolean;
  subTitle2?: any;
}

const RenderIcons: FC<Props> = (props) => {
  const { hasAttachments, hasComments, isFlagged, areIconsAttachedToRight } = props;

  if (areIconsAttachedToRight) {
    return (
      <IconSlot>
        <HealthEntryMetaIcons
          hasAttachments={hasAttachments}
          hasComments={hasComments}
          isFlagged={isFlagged}
        />
      </IconSlot>
    );
  }

  return isFlagged ? <IconSlot><div className="cmp-hesummary-flagged profile-background-color-highlight" /></IconSlot> : null;
};

const HealthEntrySummaryTemplate: FC<Props> = (props) => {
  const { title, additionalTitle, subTitle, subTitle2, areIconsAttachedToRight, color } = props;
  const mainStyleClass = classnames('cmp-hesummary',
    color ? colorClassNames.font(color) : null,
    {
      'is-white': color === 'white',
    }
  );

  return (
    <div className={mainStyleClass}>
      {!areIconsAttachedToRight && <RenderIcons {...props} />}
      <div className="cmp-hesummary-title font-body">
        {title}
        {areIconsAttachedToRight && <RenderIcons {...props} />}
      </div>

      {additionalTitle}

      <div className="cmp-hesummary-subtitle">
        {subTitle}
      </div>
      {
        subTitle2
        ? (
          <div className="cmp-hesummary-subtitle">
            {subTitle2}
          </div>
        ) : null
      }
    </div>
  );
};

export const ShortSummaryTemplate = (props) => {
  const { areIconsAttachedToRight, color } = props;
  const mainStyleClass = classnames('cmp-hesummary',
    color ? colorClassNames.font(color) : null,
    {
      'is-white': color === 'white',
    },
  );

  return (
    <div className={mainStyleClass}>
      {!areIconsAttachedToRight && <RenderIcons {...props} />}

      {
        React.Children.map(props.children, (c, i) => {
          if (i === 0) {
            return (
              <div className="cmp-hesummary-title font-body">
                {c}
                {areIconsAttachedToRight && <RenderIcons {...props} />}
              </div>
            );
          } else {
            return (
              <div className="cmp-hesummary-subtitle">
                {c}
              </div>
            );
          }
        })
      }
    </div>
  );
};

export const FileViewHealthEntrySummary: FC<Props> = (props) => {
  const { title, additionalTitle, subTitle, subTitle2, areIconsAttachedToRight, color } = props;
  const mainStyleClass = classnames('cmp-hesummary',
    color ? colorClassNames.font(color) : null,
    {
      'is-white': color === 'white',
    }
  );

  return (
    <div className={mainStyleClass}>
      <RenderIcons {...props} />
      <div className="cmp-hesummary-title font-body">
        {title}
        <FlaggedIcon />
      </div>

      {additionalTitle}

      <div className="cmp-hesummary-subtitle">
        {subTitle}
        {props.hasAttachments && <IconAttachment.Small />}
        {props.hasComments && <Icons.IconComment.Small color={COLORS.gsMedDark} />}
      </div>
      {
        subTitle2
        ? (
          <div className="cmp-hesummary-subtitle">
            {subTitle2}
          </div>
        ) : null
      }
    </div>
  );
};



export default HealthEntrySummaryTemplate;
